import React, { useEffect, useState, useCallback } from 'react';
import { X, MapPin, Loader2, RefreshCw, Plus, Trash2, Shield, AlertTriangle } from 'lucide-react';
import { apiService } from '../../services/api.service';
import { API_ENDPOINTS } from '../../config/environment';
import { fleetToast } from '../../utils/toast';
import './MemberRegionsModal.css';

const MemberRegionsModal = ({ member, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [regions, setRegions] = useState([]);
  const [coverageAreas, setCoverageAreas] = useState([]);
  const [showAssign, setShowAssign] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [loadingAreas, setLoadingAreas] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.get(API_ENDPOINTS.FLEET_PANEL.MEMBER_REGIONS(member.id));
      if (res.success) setRegions(res.data || []);
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  }, [member.id]);

  const loadCoverageAreas = async () => {
    setLoadingAreas(true);
    try {
      const res = await apiService.get(API_ENDPOINTS.FLEET_PANEL.COVERAGE_AREAS);
      if (res.success) setCoverageAreas(res.data || []);
    } catch { /* silent */ } finally {
      setLoadingAreas(false);
    }
  };

  useEffect(() => { load(); }, [load]);

  const handleOpenAssign = async () => {
    setShowAssign(true);
    if (coverageAreas.length === 0) await loadCoverageAreas();
  };

  const assignedSubRegionIds = regions.map((r) => r.subRegionId);
  const availableAreas = coverageAreas.filter((a) => !assignedSubRegionIds.includes(a.subRegionId));

  const handleAssign = async (subRegionId) => {
    setAssigning(true);
    try {
      const res = await apiService.post(API_ENDPOINTS.FLEET_PANEL.MEMBER_REGIONS(member.id), { subRegionId });
      if (res.success) {
        fleetToast.success('Bölge atandı');
        await load();
        if (availableAreas.length <= 1) setShowAssign(false);
        else await loadCoverageAreas();
      } else {
        fleetToast.error(res.error || 'Bölge atanamadı');
      }
    } catch {
      fleetToast.error('Beklenmeyen hata');
    } finally {
      setAssigning(false);
    }
  };

  const handleRemove = async (regionId) => {
    setRemoving(regionId);
    try {
      const res = await apiService.delete(API_ENDPOINTS.FLEET_PANEL.MEMBER_REGION_DELETE(member.id, regionId));
      if (res.success) {
        fleetToast.success('Bölge kaldırıldı');
        await load();
      } else {
        fleetToast.error(res.error || 'Bölge kaldırılamadı');
      }
    } catch {
      fleetToast.error('Beklenmeyen hata');
    } finally {
      setRemoving(null);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal mr-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2><MapPin size={18} /> {member.courierName} — Bölge Yönetimi</h2>
          <div className="ms-header-actions">
            <button className="btn btn-secondary btn-icon" onClick={load} disabled={loading}>
              <RefreshCw size={16} className={loading ? 'spinning' : ''} />
            </button>
            <button className="modal-close" onClick={onClose}><X size={20} /></button>
          </div>
        </div>

        <div className="modal-body">
          {loading && (
            <div className="page-loading" style={{ padding: '40px 0' }}>
              <Loader2 className="spinning" size={28} />
              <p>Yükleniyor...</p>
            </div>
          )}

          {!loading && regions.length === 0 && !showAssign && (
            <div className="mr-empty">
              <div className="mr-empty-icon"><AlertTriangle size={32} /></div>
              <h3>Bölge atanmamış</h3>
              <p>Bu kuryeye henüz çalışma bölgesi atanmamış. Sipariş alabilmesi için en az bir bölge gereklidir.</p>
              <button className="mr-assign-cta" onClick={handleOpenAssign}>
                <Plus size={16} /> Bölge Ata
              </button>
            </div>
          )}

          {!loading && regions.length > 0 && (
            <>
              <div className="mr-section-header">
                <div className="mr-section-title">
                  <Shield size={14} />
                  <span>Atanmış Bölgeler</span>
                  <span className="mr-count">{regions.length}</span>
                </div>
                <button className="mr-add-btn" onClick={handleOpenAssign} disabled={showAssign}>
                  <Plus size={14} /> Bölge Ekle
                </button>
              </div>

              <div className="mr-list">
                {regions.map((r) => (
                  <div key={r.id} className="mr-card">
                    <div className="mr-card-icon"><MapPin size={16} /></div>
                    <div className="mr-card-body">
                      <div className="mr-card-name">{r.subRegionName || r.regionName || '—'}</div>
                      <div className="mr-card-meta">
                        {r.regionName && r.subRegionName && <span>{r.regionName}</span>}
                        {r.provinceName && <span>{r.provinceName}</span>}
                        {r.districtName && <span>{r.districtName}</span>}
                      </div>
                    </div>
                    <button
                      className="mr-remove-btn"
                      onClick={() => handleRemove(r.id)}
                      disabled={removing === r.id || regions.length <= 1}
                      title={regions.length <= 1 ? 'Son bölge kaldırılamaz' : 'Bölgeyi kaldır'}
                    >
                      {removing === r.id ? <Loader2 size={14} className="spinning" /> : <Trash2 size={14} />}
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}

          {showAssign && (
            <div className="mr-assign-panel">
              <div className="mr-section-header">
                <div className="mr-section-title">
                  <Plus size={14} />
                  <span>Filo Kapsama Alanları</span>
                </div>
                <button className="mr-cancel-btn" onClick={() => setShowAssign(false)}>İptal</button>
              </div>

              {loadingAreas && (
                <div className="page-loading" style={{ padding: '20px 0' }}>
                  <Loader2 className="spinning" size={20} />
                </div>
              )}

              {!loadingAreas && availableAreas.length === 0 && (
                <div className="mr-assign-empty">Atanabilecek bölge kalmadı</div>
              )}

              {!loadingAreas && availableAreas.length > 0 && (
                <div className="mr-area-grid">
                  {availableAreas.map((a) => (
                    <button
                      key={a.id}
                      className="mr-area-card"
                      onClick={() => handleAssign(a.subRegionId)}
                      disabled={assigning}
                    >
                      <MapPin size={14} />
                      <div className="mr-area-info">
                        <span className="mr-area-name">{a.subRegionName || a.regionName || '—'}</span>
                        {a.regionName && a.subRegionName && (
                          <span className="mr-area-parent">{a.regionName}</span>
                        )}
                      </div>
                      <Plus size={14} className="mr-area-plus" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MemberRegionsModal;
